import { ref } from 'vue';
import { useApi } from './useApi';
import { useAppState } from './useAppState';
import { getHttpInstance, getApiConfig } from './useApi';
// Set true by pages that add/update/delete a credit account so the next
// credit-info read (e.g. MoneyPage) knows to SWR+poll until the cached
// /CreditAccounts payload reflects the write.
export const pendingCreditRevalidate = ref(false);
// Dedupe in-flight pollUntilChanged cycles so concurrent callers attach to the
// existing poll instead of each starting their own loop.
let creditInfoPolling = false;
const creditInfoWaiters = [];
// Stable change key for the credit-info SWR poll, used only as a fallback
// when the server hash is unavailable. Built from just the sorted account ids
// so adding/removing an account flips the key, while volatile fields (balances)
// never bail the poll out early.
export function creditInfoChangeKey(info) {
    return (info ?? []).map((a) => a.id).sort().join(',');
}
export function useCredit() {
    const api = useApi();
    const state = useAppState();
    function getCreditInfo(onSuccess = null, refreshCache = false, staleWhileRevalidate = false, pollUntilChanged = false) {
        const url = "/Customers/00000000-0000-0000-0000-000000000000/CreditAccounts";
        // A pollUntilChanged cycle is already running — attach this caller's
        // callback to it rather than starting a second poll loop. Fire the stale
        // emission synchronously from state.creditInfo so the new caller gets
        // the cached payload immediately (matches the SWR contract).
        if (pollUntilChanged && creditInfoPolling) {
            if (onSuccess) {
                if (state.creditInfo)
                    onSuccess(/* isStale */ true);
                creditInfoWaiters.push(onSuccess);
            }
            return;
        }
        if (pollUntilChanged)
            creditInfoPolling = true;
        api.GET(url, (result, isStale) => {
            state.creditInfo = result;
            if (onSuccess)
                onSuccess(isStale);
            if (!isStale && pollUntilChanged) {
                creditInfoPolling = false;
                const waiters = creditInfoWaiters.splice(0);
                waiters.forEach(cb => cb(/* isStale */ false));
            }
        }, !refreshCache, refreshCache, /* ttlMins */ undefined, /* retrying */ false, staleWhileRevalidate, pollUntilChanged, creditInfoChangeKey);
    }
    function getCreditProviders(onSuccess = null) {
        api.GET("/CreditProviders", (result) => {
            state.creditProviders = result;
            if (onSuccess)
                onSuccess(result);
        }, true);
    }
    function getCreditTypes(onSuccess = null) {
        api.GET("/CreditTypes", (result) => {
            state.creditTypes = result;
            if (onSuccess)
                onSuccess(result);
        }, true);
    }
    function getCreditAccount(id, onSuccess = null, refreshCache = false, staleWhileRevalidate = false, pollUntilChanged = false, pollIntervalMs) {
        api.GET("/Customers/00000000-0000-0000-0000-000000000000/CreditAccounts/" + id, (result, isStale) => {
            state.creditAccount = result;
            if (onSuccess)
                onSuccess(result, isStale);
        }, !refreshCache, refreshCache, undefined, false, staleWhileRevalidate, pollUntilChanged, undefined, pollIntervalMs);
    }
    function addCreditAccount(credit, onSuccess = null) {
        getHttpInstance().post(getApiConfig().api + "/Customers/00000000-0000-0000-0000-000000000000/CreditAccounts", credit)
            .then((response) => { if (onSuccess)
            onSuccess(response.data); })
            .catch((result) => {
            if (result.response && result.response.status == 401) {
                api.refreshLogin(() => addCreditAccount(credit, onSuccess));
            }
        });
    }
    function updateCreditAccount(credit, onSuccess = null) {
        getHttpInstance().put(getApiConfig().api + "/Customers/00000000-0000-0000-0000-000000000000/CreditAccounts/" + credit.id, credit)
            .then((response) => { if (onSuccess)
            onSuccess(response.data); })
            .catch((result) => {
            if (result.response && result.response.status == 401) {
                api.refreshLogin(() => updateCreditAccount(credit, onSuccess));
            }
        });
    }
    function deleteCreditAccount(id, onSuccess = null) {
        getHttpInstance().delete(getApiConfig().api + "/Customers/00000000-0000-0000-0000-000000000000/CreditAccounts/" + id)
            .then((response) => { if (onSuccess)
            onSuccess(response.data); })
            .catch((result) => {
            if (result.response && result.response.status == 401) {
                api.refreshLogin(() => deleteCreditAccount(id, onSuccess));
            }
        });
    }
    return {
        getCreditInfo,
        getCreditProviders,
        getCreditTypes,
        getCreditAccount,
        addCreditAccount,
        updateCreditAccount,
        deleteCreditAccount,
    };
}
